import { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Card, CardContent, CardMedia, Container, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import { openDB } from 'idb';
import { HistoryItem } from '../types';

export default function HistoryPage() {
  const [history, setHistory] = useState<HistoryItem[]>([]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const db = await openDB('minds-eye', 1, {
          upgrade(db) {
            if (!db.objectStoreNames.contains('history')) {
              db.createObjectStore('history', { keyPath: 'id' });
            }
          },
        });

        const items = await db.getAll('history');
        setHistory(items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      } catch (error) {
        console.error('Error loading history:', error);
      }
    };

    loadHistory();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      const db = await openDB('minds-eye', 1);
      await db.delete('history', id);
      setHistory(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting history item:', error);
    }
  };

  const handleShare = async (image: string, description: string) => {
    try {
      // If it's a data URL (base64 image), we need to convert it to a blob first
      if (image.startsWith('data:')) {
        const response = await fetch(image);
        const blob = await response.blob();
        
        if (navigator.share) {
          const file = new File([blob], 'generated-image.png', { type: 'image/png' });
          await navigator.share({
            title: 'Generated Image',
            text: description,
            files: [file],
          });
        } else {
          // For non-share API browsers, create a temporary link
          const url = URL.createObjectURL(blob);
          await navigator.clipboard.writeText(url);
          alert('Image URL copied to clipboard!');
          URL.revokeObjectURL(url);
        }
      } else {
        // For regular URLs
        if (navigator.share) {
          await navigator.share({
            title: 'Generated Image',
            text: description,
            url: image,
          });
        } else {
          await navigator.clipboard.writeText(image);
          alert('Image URL copied to clipboard!');
        }
      }
    } catch (error) {
      console.error('Error sharing:', error);
      alert('Failed to share image. Please try again.');
    }
  };

  return (
    <Container maxWidth="sm" sx={{ 
      height: '90vh',
      display: 'flex',
      flexDirection: 'column',
      py: 2
    }}>
      <Typography variant="h5" sx={{ mb: 3 }}>History</Typography>

      {history.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box>
            <Typography variant="h6" color="text.secondary">
              No History
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your generated images and descriptions will appear here
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ 
          flex: 1,
          overflowY: 'auto',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {history.map((item) => (
              <Card key={item.id} sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  image={item.image}
                  alt="Generated image"
                  sx={{ 
                    width: '100%',
                    height: 'auto',
                    maxHeight: '80vh',
                    objectFit: 'contain'
                  }}
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                </CardContent>
                <Box sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8,
                  display: 'flex',
                  gap: 1
                }}>
                  <Tooltip title="Share">
                    <IconButton
                      onClick={() => handleShare(item.image, item.description)}
                      sx={{ 
                        bgcolor: 'rgba(0,0,0,0.5)',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.7)',
                        }
                      }}
                    >
                      <ShareIcon sx={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDelete(item.id)}
                      sx={{ 
                        bgcolor: 'rgba(0,0,0,0.5)',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.7)',
                        }
                      }}
                    >
                      <DeleteIcon sx={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Container>
  );
} 